import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
	CheckCircle,
	Clock3,
	ExternalLink,
	Image as ImageIcon,
	Loader,
	Megaphone,
	Pencil,
	Plus,
	Power,
	RefreshCw,
	Save,
	Trash2,
	Upload,
	XCircle
} from 'lucide-react';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import Input from '../../components/Input';
import { ContainerSpinner } from '../../components/Spinner';
import { StorePopup } from '../../components/StorePopup';
import Textarea from '../../components/Textarea';
import { GlobalContext } from '../../context/GlobalContext';
import { callAPI, type StorePopupPayload, type StorePopupRecord } from '../../hooks/api';
import { Button } from '../../shadcn/Button';
import { Switch } from '../../shadcn/Switch';
import { COUNTRIES, COUNTRY_LABELS } from '../../utils/constants';

type EditablePopup = StorePopupRecord & { is_new?: boolean };

const emptyPopup = (country: string): EditablePopup => ({
	id: `draft-${Date.now()}`,
	country,
	title: '',
	subtitle: '',
	image_url: null,
	redirect_url: null,
	button_text: null,
	is_active: false,
	master_stores_only: false,
	append_store_email_to_url: false,
	dismiss_interval_days: 3,
	display_version: 1,
	is_new: true
});

const isValidHttpUrl = (value: string) => {
	try {
		return ['http:', 'https:'].includes(new URL(value).protocol);
	} catch {
		return false;
	}
};

const formatDate = (value?: string) => {
	if (!value) return 'Sin fecha';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return 'Sin fecha';
	return new Intl.DateTimeFormat('es', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

export default function StoreBanner() {
	const { internalCountry, userData } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';
	const countryRef = useRef(country);
	countryRef.current = country;
	const [popups, setPopups] = useState<StorePopupRecord[]>([]);
	const [popup, setPopup] = useState<EditablePopup>(() => emptyPopup(country));
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [togglingId, setTogglingId] = useState<string | null>(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);

	const loadPopups = useCallback(
		async (preferredId?: string) => {
			const targetCountry = country;
			setLoading(true);
			const [response, error] = await callAPI(
				'https://api.krece.app/administration/store-popups/',
				'GET',
				{ country: targetCountry },
				userData,
				targetCountry,
				true,
				false
			);
			if (countryRef.current !== targetCountry) return;
			if (error || !response) {
				setAlertDialog({ title: 'Error', message: error || 'No se pudo cargar el historial de pop-ups.' });
				setLoading(false);
				return;
			}
			setPopups(response);
			setPopup((current) => {
				const selectedId = preferredId || (!current.is_new ? current.id : undefined);
				return (
					response.find((item) => item.id === selectedId) ||
					response.find((item) => item.is_active) ||
					response[0] ||
					emptyPopup(targetCountry)
				);
			});
			setLoading(false);
		},
		[country, userData]
	);

	useEffect(() => {
		setPopups([]);
		setPopup(emptyPopup(country));
		loadPopups();
	}, [country, loadPopups]);

	const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
			setAlertDialog({ title: 'Formato no compatible', message: 'Sube una imagen JPG, PNG o WebP.' });
			event.target.value = '';
			return;
		}
		if (file.size > 2000000) {
			setAlertDialog({ title: 'Imagen demasiado grande', message: 'La imagen no puede superar los 2 MB.' });
			event.target.value = '';
			return;
		}

		const targetPopupId = popup.id;
		const targetCountry = country;
		setUploadingImage(true);
		const formData = new FormData();
		formData.append('country', targetCountry);
		formData.append('image', file);
		const [response, error] = await callAPI(
			'https://api.krece.app/administration/store-popups/upload-image/',
			'POST',
			formData,
			userData,
			targetCountry
		);
		setUploadingImage(false);
		event.target.value = '';
		if (error || !response?.image_url) {
			setAlertDialog({ title: 'Error', message: error || 'No se pudo subir la imagen. Intenta nuevamente.' });
			return;
		}
		setPopup((current) =>
			current.country === targetCountry && current.id === targetPopupId ? { ...current, image_url: response.image_url } : current
		);
	};

	const validatePopup = () => {
		if (!popup.title.trim()) return 'Ingresa el título del pop-up.';
		if (popup.redirect_url?.trim() && !isValidHttpUrl(popup.redirect_url.trim())) {
			return 'Ingresa una URL completa que comience con http:// o https://.';
		}
		if (popup.dismiss_interval_days < 1 || popup.dismiss_interval_days > 365) {
			return 'La frecuencia debe estar entre 1 y 365 días.';
		}
		return null;
	};

	const buildPayload = (): StorePopupPayload => ({
		...((popup.is_new || !popup.id) && { country }),
		title: popup.title.trim(),
		subtitle: popup.subtitle?.trim() || null,
		image_url: popup.image_url?.trim() || null,
		redirect_url: popup.redirect_url?.trim() || null,
		button_text: popup.redirect_url?.trim() ? popup.button_text?.trim() || null : null,
		is_active: popup.is_active,
		master_stores_only: popup.master_stores_only,
		append_store_email_to_url: popup.redirect_url?.trim() ? popup.append_store_email_to_url : false,
		dismiss_interval_days: popup.dismiss_interval_days
	});

	const savePopup = async () => {
		if (uploadingImage) return;
		const validationError = validatePopup();
		if (validationError) {
			setAlertDialog({ title: 'Revisa la configuración', message: validationError });
			return;
		}

		setSaving(true);
		let response: StorePopupRecord | null = null;
		let error: string | null = null;
		if (popup.is_new) {
			[response, error] = await callAPI(
				'https://api.krece.app/administration/store-popups/',
				'POST',
				buildPayload() as Required<Pick<StorePopupPayload, 'country' | 'title' | 'is_active' | 'dismiss_interval_days'>> &
					StorePopupPayload,
				userData,
				country
			);
		} else {
			const endpoint =
				`https://api.krece.app/administration/store-popups/${popup.id}/` as 'https://api.krece.app/administration/store-popups/<popup_id>/';
			[response, error] = await callAPI(endpoint, 'PATCH', buildPayload(), userData, country);
		}
		setSaving(false);
		if (error || !response) {
			setAlertDialog({ title: 'Error', message: error || 'No se pudo guardar el pop-up.' });
			return;
		}
		await loadPopups(response.id);
		setAlertDialog({ title: 'Cambios guardados', message: 'El pop-up se guardó correctamente.' });
	};

	const updatePopup = async (item: StorePopupRecord, payload: StorePopupPayload, successMessage: string) => {
		setTogglingId(item.id);
		const endpoint =
			`https://api.krece.app/administration/store-popups/${item.id}/` as 'https://api.krece.app/administration/store-popups/<popup_id>/';
		const [response, error] = await callAPI(endpoint, 'PATCH', payload, userData, country);
		setTogglingId(null);
		if (error || !response) {
			setAlertDialog({ title: 'Error', message: error || 'No se pudo actualizar el pop-up.' });
			return;
		}
		await loadPopups(response.id);
		setAlertDialog({ title: 'Listo', message: successMessage });
	};

	if (loading && popups.length === 0) return <ContainerSpinner size={3} />;

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}

			<div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
				<div>
					<h1 className="text-2xl font-bold text-neutral-100">Pop-ups para tiendas</h1>
					<p className="mt-1 text-sm text-neutral-400">
						Administra el pop-up activo y su historial en {COUNTRY_LABELS[country as (typeof COUNTRIES)[number]]}.
					</p>
				</div>
				<Button variant="primary" className="!w-auto self-start" onClick={() => setPopup(emptyPopup(country))}>
					<Plus className="size-4" /> Nuevo pop-up
				</Button>
			</div>

			<div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
				<section className="space-y-6 rounded-xl border border-neutral-800 bg-neutral-1000 p-4 md:p-6">
					<div className="flex items-center justify-between gap-4">
						<div>
							<h2 className="text-lg font-semibold text-neutral-100">{popup.is_new ? 'Nuevo pop-up' : 'Editar pop-up'}</h2>
							<p className="mt-0.5 text-xs text-neutral-500">
								Activarlo apagará automáticamente cualquier otro pop-up activo del país.
							</p>
						</div>
						<Switch
							checked={popup.is_active}
							onCheckedChange={(is_active) => setPopup((current) => ({ ...current, is_active }))}
						/>
					</div>

					<div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-925 p-4">
						<div>
							<p className="text-sm font-medium text-neutral-100">Solo tiendas master</p>
							<p className="mt-0.5 text-xs text-neutral-400">
								Si está activo, el pop-up no aparecerá a sucursales ni vendedores.
							</p>
						</div>
						<Switch
							checked={popup.master_stores_only}
							onCheckedChange={(master_stores_only) => setPopup((current) => ({ ...current, master_stores_only }))}
						/>
					</div>

					<div>
						<Input
							floatingLabel="Título"
							value={popup.title}
							maxLength={80}
							placeholder="Nuevas tasas de financiamiento"
							onChange={(title) => setPopup((current) => ({ ...current, title }))}
						/>
						<p className="mt-1 text-right text-xs text-neutral-500">{popup.title.length}/80</p>
					</div>
					<div>
						<Textarea
							floatingLabel="Subtítulo (opcional)"
							value={popup.subtitle || ''}
							maxLength={200}
							rows={4}
							resize="vertical"
							placeholder="Información adicional para las tiendas"
							onChange={(subtitle) => setPopup((current) => ({ ...current, subtitle }))}
						/>
						<p className="mt-1 text-right text-xs text-neutral-500">{popup.subtitle?.length || 0}/200</p>
					</div>

					<div className="space-y-3 border-t border-neutral-800 pt-5">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm font-medium text-neutral-200">Imagen opcional</p>
								<p className="mt-0.5 text-xs text-neutral-500">JPG, PNG o WebP · máximo 2 MB · recomendado 16:9</p>
							</div>
							{popup.image_url && !uploadingImage && (
								<button
									type="button"
									className="text-xs font-medium text-red-400 hover:text-red-300"
									onClick={() => setPopup((current) => ({ ...current, image_url: null }))}
								>
									<Trash2 className="mr-1 inline size-3.5" />
									Eliminar
								</button>
							)}
						</div>
						<input
							id="store-popup-image"
							type="file"
							accept="image/jpeg,image/png,image/webp"
							className="hidden"
							onChange={handleImageUpload}
							disabled={uploadingImage || saving}
						/>
						<label
							htmlFor="store-popup-image"
							className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-700 bg-neutral-925 p-5 transition-colors hover:border-primary-500 ${uploadingImage ? 'pointer-events-none opacity-60' : ''}`}
						>
							{uploadingImage ? (
								<>
									<Loader className="mb-2 size-7 animate-spin text-primary-500" />
									<p className="text-sm text-neutral-300">Subiendo imagen…</p>
								</>
							) : popup.image_url ? (
								<>
									<img src={popup.image_url} alt="" className="mb-3 h-24 w-40 rounded-lg object-cover" />
									<p className="text-sm font-medium text-neutral-300">Cambiar imagen</p>
								</>
							) : (
								<>
									<Upload className="mb-2 size-7 text-neutral-400" />
									<p className="text-sm font-medium text-neutral-300">Subir imagen</p>
								</>
							)}
						</label>
					</div>

					<div className="space-y-4 border-t border-neutral-800 pt-5">
						<Input
							type="url"
							floatingLabel="URL de destino (opcional)"
							value={popup.redirect_url || ''}
							maxLength={500}
							placeholder="https://krece.app/promocion"
							onChange={(redirect_url) =>
								setPopup((current) => ({
									...current,
									redirect_url,
									...(!redirect_url.trim() && { append_store_email_to_url: false })
								}))
							}
						/>
						{popup.redirect_url?.trim() && (
							<>
								<Input
									floatingLabel="Texto del botón"
									value={popup.button_text || ''}
									maxLength={30}
									placeholder="Ver más"
									onChange={(button_text) => setPopup((current) => ({ ...current, button_text }))}
								/>
								<div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-925 p-4">
									<div>
										<p className="text-sm font-medium text-neutral-100">Añadir ID de la tienda al enlace</p>
										<p className="mt-0.5 text-xs text-neutral-400">
											Agrega <code>ID=email</code> al URL. Actívalo solo para destinos de confianza porque comparte el correo de
											sesión.
										</p>
									</div>
									<Switch
										checked={popup.append_store_email_to_url}
										onCheckedChange={(append_store_email_to_url) =>
											setPopup((current) => ({ ...current, append_store_email_to_url }))
										}
									/>
								</div>
							</>
						)}
						<div>
							<Input
								type="number"
								allowedChars="numbers_only"
								floatingLabel="Volver a mostrar después de (días)"
								value={String(popup.dismiss_interval_days)}
								placeholder="3"
								onChange={(value) => setPopup((current) => ({ ...current, dismiss_interval_days: Number(value) || 1 }))}
							/>
							<p className="mt-1 text-xs text-neutral-500">El cierre se recuerda en ese navegador durante este período.</p>
						</div>
					</div>

					<div className="flex flex-wrap justify-end gap-3 border-t border-neutral-800 pt-5">
						{!popup.is_new && (
							<Button
								variant="secondary"
								className="!w-auto"
								disabled={!!togglingId}
								onClick={() =>
									updatePopup(
										popup,
										{ reset_frequency: true },
										'El pop-up volverá a aparecer en los navegadores aunque lo hubieran cerrado.'
									)
								}
							>
								<RefreshCw className="size-4" /> Hacer que vuelva a aparecer
							</Button>
						)}
						<Button variant="primary" className="!w-auto" onClick={savePopup} loading={saving} disabled={uploadingImage}>
							<Save className="size-4" /> Guardar
						</Button>
					</div>
				</section>

				<aside className="space-y-3 xl:sticky xl:top-6 xl:self-start">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold text-neutral-100">Vista previa</h2>
						<span className="inline-flex items-center gap-1 text-xs text-neutral-500">
							{popup.redirect_url?.trim() ? (
								<>
									<ExternalLink className="size-3.5" /> Con enlace
								</>
							) : (
								<>
									<ImageIcon className="size-3.5" /> Informativo
								</>
							)}
						</span>
					</div>
					{popup.title.trim() ? (
						<StorePopup popup={{ ...popup, is_active: true }} country={country} storeEmail="tienda@ejemplo.com" preview />
					) : (
						<div className="flex min-h-[420px] flex-col items-center justify-center rounded-xl border border-neutral-800 bg-neutral-1000 p-8 text-center">
							<Megaphone className="mb-3 size-8 text-neutral-500" />
							<p className="font-medium text-neutral-200">Completa el título para ver el pop-up</p>
						</div>
					)}
				</aside>
			</div>

			<section className="space-y-3">
				<div className="flex items-end justify-between gap-4">
					<div>
						<h2 className="text-lg font-semibold text-neutral-100">Historial</h2>
						<p className="mt-0.5 text-xs text-neutral-500">Los pop-ups anteriores se conservan y pueden volver a activarse.</p>
					</div>
					<span className="text-sm text-neutral-500">{popups.length} en total</span>
				</div>
				{popups.length === 0 ? (
					<div className="rounded-xl border border-neutral-800 bg-neutral-1000">
						<EmptyState icon={Megaphone} title="No hay pop-ups" subtitle="Crea el primero para comenzar." />
					</div>
				) : (
					<div className="space-y-3">
						{popups.map((item) => (
							<article
								key={item.id}
								className={`flex flex-col gap-4 rounded-xl border bg-neutral-1000 p-4 sm:flex-row sm:items-center ${item.is_active ? 'border-primary-500/60' : 'border-neutral-800'}`}
							>
								{item.image_url ? (
									<img src={item.image_url} alt="" className="h-16 w-24 flex-shrink-0 rounded-lg object-cover" />
								) : (
									<div className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-925">
										<ImageIcon className="size-5 text-neutral-500" />
									</div>
								)}
								<div className="min-w-0 flex-1">
									<div className="mb-1 flex flex-wrap items-center gap-2">
										{item.is_active ? (
											<span className="inline-flex items-center gap-1 text-xs font-medium text-green-400">
												<CheckCircle className="size-3.5" /> Activo
											</span>
										) : (
											<span className="inline-flex items-center gap-1 text-xs font-medium text-neutral-500">
												<XCircle className="size-3.5" /> Inactivo
											</span>
										)}
										<span className="inline-flex items-center gap-1 text-xs text-neutral-500">
											<Clock3 className="size-3.5" /> {formatDate(item.created_at)}
										</span>
									</div>
									<h3 className="truncate text-sm font-semibold text-neutral-100">{item.title}</h3>
									<p className="mt-1 line-clamp-2 text-xs text-neutral-400">{item.subtitle || 'Sin subtítulo'}</p>
								</div>
								<div className="flex items-center justify-end gap-2 border-t border-neutral-800 pt-3 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
									<button
										type="button"
										onClick={() => setPopup({ ...item })}
										className="rounded-lg p-2 text-neutral-400 hover:bg-neutral-925 hover:text-neutral-100"
										title="Editar"
									>
										<Pencil className="size-4" />
									</button>
									<Button
										variant={item.is_active ? 'secondary' : 'primary'}
										className="!w-auto"
										loading={togglingId === item.id}
										disabled={!!togglingId}
										onClick={() =>
											updatePopup(
												item,
												{ is_active: !item.is_active },
												item.is_active ? 'El pop-up quedó inactivo.' : 'El pop-up quedó activo y los demás se apagaron.'
											)
										}
									>
										<Power className="size-4" /> {item.is_active ? 'Apagar' : 'Activar'}
									</Button>
								</div>
							</article>
						))}
					</div>
				)}
			</section>
		</div>
	);
}
